import api, { currentUser } from './api';

/** Same role codes and groups as the server (backend/core/rights.py). */
export const ROLE_LABELS = {
  ADMIN: 'Admin',
  MANAGER: 'General manager',
  SCHOOL_MANAGER: 'School manager',
  LIVERY_MANAGER: 'Stable & livery manager',
  STABLE_MANAGER: 'Stable & livery manager',
  CASHIER: 'Cashier',
  ACCOUNTANT: 'Accountant',
  COACH: 'Coach',
  SECURITY: 'Security',
  DATAENTRY: 'Store / data entry',
  OFFICE: 'General user',
  FARRIER: 'Farrier / yard staff',
  PURCHASER: 'Purchase manager',
  HEAD_GROOM: 'Head of grooms',
};

export const GM = ['MANAGER'];
export const SCHOOL_MGRS = ['MANAGER', 'SCHOOL_MANAGER'];
export const LIVERY_MGRS = ['MANAGER', 'LIVERY_MANAGER'];
export const MGMT = ['MANAGER', 'SCHOOL_MANAGER', 'LIVERY_MANAGER'];
export const FINANCE = ['MANAGER', 'ACCOUNTANT'];
export const NO_MONEY = ['SECURITY', 'COACH', 'FARRIER', 'HEAD_GROOM'];

/** Main role plus extra roles of the signed-in user. */
export const userRoles = (user = currentUser()) => {
  if (!user) return [];
  return user.roles && user.roles.length ? user.roles : [user.role].filter(Boolean);
};

export const hasRole = (...roles) => {
  const want = roles.flat();
  return userRoles().some((r) => want.includes(r));
};

export const isAdmin = () => hasRole('ADMIN');

/** Admin passes every role check, like on the server. */
export const can = (...roles) => isAdmin() || hasRole(...roles);

export const seesMoney = () => {
  const mine = userRoles();
  return !mine.length || !mine.every((r) => NO_MONEY.includes(r));
};

export const roleLabel = (user = currentUser()) => {
  if (!user) return '';
  const labels = [user.role, ...userRoles(user)].map((r) => ROLE_LABELS[r] || r);
  return [...new Set(labels)].join(' + ');
};

const LANDING = {
  ADMIN: '/horses',
  MANAGER: '/reports/gm-dashboard',
  SCHOOL_MANAGER: '/timetable',
  LIVERY_MANAGER: '/livery',
  STABLE_MANAGER: '/feed-sheet',
  CASHIER: '/school/day-end',
  ACCOUNTANT: '/school-control',
  COACH: '/coach-day',
  SECURITY: '/gate',
  DATAENTRY: '/lpos',
  OFFICE: '/timetable',
  FARRIER: '/service-records',
  PURCHASER: '/lpos',
  HEAD_GROOM: '/pony-rides',
};

/** The first screen of the user's main role. */
export const landingPath = () => LANDING[currentUser()?.role] || '/horses';

/* Screens & tabs the Admin allows per role (Screens & Roles). Keys: menu path, tabs "path#tab". */
const SCREENS_KEY = 'screens';
const MENU_KEY = 'menu';

export const loadScreens = async () => {
  const r = await api.get('/auth/screens/');
  localStorage.setItem(SCREENS_KEY, JSON.stringify(r.data.hidden || []));
  if (Array.isArray(r.data.menu)) {
    localStorage.setItem(MENU_KEY, JSON.stringify(r.data.menu));
  }
  return r.data;
};

/** Menu groups from the server (Screens registry). null until loaded. */
export const serverMenu = () => {
  try {
    const m = JSON.parse(localStorage.getItem(MENU_KEY) || 'null');
    return Array.isArray(m) ? m : null;
  } catch {
    return null;
  }
};

const hiddenScreens = () => {
  try {
    return JSON.parse(localStorage.getItem(SCREENS_KEY) || 'null');
  } catch {
    return null;
  }
};

/** null until the list is loaded from the server. */
export const screensLoaded = () => Array.isArray(hiddenScreens());

export const canSee = (key) => {
  if (isAdmin()) return true;
  const hidden = hiddenScreens();
  return !Array.isArray(hidden) || !hidden.includes(key);
};

/** Tabs of a page the user may see; tab keys are "<page path>#<tab key>". */
export const visibleTabs = (page, items) => items.filter((t) => canSee(`${page}#${t.key}`));
